import { useEffect, useRef, useState } from 'react';
import { Button, Form, ListGroup } from 'react-bootstrap';
import { addressAutocomplete, addressPlaceDetails, addressReverseGeocode } from '../api';
import MapLink from './MapLink';

// Address entry with Google-Maps search + "detect my location", used on every address form
// (booking delivery address, owner pickup address, delivery-person onboarding, admin staff
// create). Backed by rentalapi's /api/maps/* proxy so no Google key is ever in the browser.
// See Docs/requirements/jewelry_rental/delivery/delivery_ticket_pickup_drop_distance.md.
//
// Degrades to exactly the plain structured fields when the proxy returns nothing (no key /
// Google down) - the search box just never shows results, and typing still works.
//
// value  : { line1, line2, area, city, state, pincode, lat, lng, placeId, formatted }
// onChange(nextValue) : called with the merged address on every edit / pick / detect.
// showState : render the State field (person addresses) - off for booking delivery address.

const GEO_FIELDS = ['lat', 'lng', 'placeId', 'formatted'];
// v5_fix_issues #11 - editing the exact door / flat / landmark doesn't move the pin, so the
// picked coordinates (and distance-based delivery pricing) survive. Only a change to the
// locality fields below invalidates them.
const LOCALITY_FIELDS = ['area', 'city', 'state', 'pincode'];

function newSessionToken() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch { /* ignore */ }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function AddressAutocomplete({
  value = {}, onChange, showState = false, size, idPrefix = 'addr', disabled = false, required = false,
}) {
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [notice, setNotice] = useState('');
  const sessionToken = useRef(newSessionToken());
  const blurTimer = useRef(null);

  // Debounced autocomplete. < 3 chars → nothing (matches the server short-circuit).
  useEffect(() => {
    if (query.trim().length < 3) {
      setPredictions([]);
      setOpen(false);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const list = await addressAutocomplete(query.trim(), sessionToken.current);
        setPredictions(list);
        setOpen(list.length > 0);
      } catch {
        setPredictions([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  // Merge a resolved address (from a pick or a detect) onto value, keeping fields Google
  // didn't return (e.g. line2) unless the resolved address has its own value.
  const applyResolved = (addr) => {
    if (!addr) return;
    onChange({
      ...value,
      line1: addr.line1 || value.line1 || '',
      line2: addr.line2 || value.line2 || '',
      area: addr.area || '',
      city: addr.city || '',
      state: addr.state || '',
      pincode: addr.pincode || '',
      lat: addr.lat ?? null,
      lng: addr.lng ?? null,
      placeId: addr.placeId || '',
      formatted: addr.formatted || '',
    });
  };

  const handlePick = async (prediction) => {
    setOpen(false);
    setQuery('');
    setLoading(true);
    try {
      const addr = await addressPlaceDetails(prediction.placeId, sessionToken.current);
      applyResolved(addr);
      if (!addr) setNotice('Could not load that address — please fill it in below.');
    } catch {
      setNotice('Could not load that address — please fill it in below.');
    } finally {
      sessionToken.current = newSessionToken();
      setLoading(false);
    }
  };

  const handleDetect = () => {
    setNotice('');
    if (!navigator.geolocation) {
      setNotice('Location is not available in this browser.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const addr = await addressReverseGeocode(pos.coords.latitude, pos.coords.longitude);
          if (addr) applyResolved(addr);
          else setNotice('Could not find an address for your location — please fill it in below.');
        } catch {
          setNotice('Could not look up your location — please fill it in below.');
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        setNotice('Location permission denied — please fill in the address below.');
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // v5_fix_issues #11 - only a change to a LOCALITY field (area/city/state/pincode) invalidates
  // the picked coordinates. Refining line1 / line2 / landmark keeps the pin, so the customer can
  // add their exact door/flat number without losing distance-based delivery pricing.
  const setField = (field) => (e) => {
    const next = { ...value, [field]: e.target.value };
    if (LOCALITY_FIELDS.includes(field)) {
      GEO_FIELDS.forEach((g) => { next[g] = g === 'lat' || g === 'lng' ? null : ''; });
    }
    onChange(next);
  };

  const field = (name, label, opts = {}) => (
    <Form.Group className="mb-2" controlId={`${idPrefix}-${name}`}>
      <Form.Label className="small">{label}</Form.Label>
      <Form.Control
        size={size}
        value={value[name] || ''}
        onChange={setField(name)}
        disabled={disabled}
        required={opts.required}
      />
    </Form.Group>
  );

  return (
    <div>
      <div className="d-flex gap-2 align-items-start mb-2">
        <div className="position-relative flex-grow-1">
          <Form.Control
            size={size}
            type="search"
            placeholder="Search for an address…"
            value={query}
            disabled={disabled}
            onChange={(e) => { setQuery(e.target.value); setNotice(''); }}
            onFocus={() => predictions.length > 0 && setOpen(true)}
            onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
            aria-label="Search for an address"
            autoComplete="off"
          />
          {open && predictions.length > 0 && (
            <ListGroup
              className="position-absolute w-100 shadow-sm"
              style={{ zIndex: 1056, maxHeight: 260, overflowY: 'auto' }}
            >
              {predictions.map((p) => (
                <ListGroup.Item
                  key={p.placeId}
                  action
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePick(p)}
                >
                  <div className="small fw-semibold">{p.mainText}</div>
                  {p.secondaryText && <div className="small text-muted">{p.secondaryText}</div>}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
        <Button
          size={size || 'sm'}
          variant="outline-secondary"
          onClick={handleDetect}
          disabled={disabled || locating}
          className="text-nowrap"
        >
          {locating ? 'Locating…' : '📍 Detect my location'}
        </Button>
      </div>
      {loading && <div className="small text-muted mb-1">Searching…</div>}
      {notice && <div className="small text-warning mb-1">{notice}</div>}

      {/* v5_fix_issues #11 - once a place is picked / detected, show it on a map so the customer
          can check it, then still refine the exact door details below without losing the pin. */}
      {value.lat != null && value.lng != null && value.lat !== '' && (
        <div className="mb-2">
          <MapLink
            address={{ lat: value.lat, lng: value.lng }}
            title="Detected location"
            label="📍 Show detected location"
          />
        </div>
      )}

      {field('line1', 'Address line 1 (house / flat / building)', { required })}
      {field('line2', 'Address line 2 (optional)')}
      {field('landmark', 'Landmark / delivery instructions (optional)')}
      <div className="row g-2">
        <div className="col-sm-6">{field('area', 'Area / locality')}</div>
        <div className="col-sm-6">{field('city', 'City', { required })}</div>
        {showState && <div className="col-sm-6">{field('state', 'State')}</div>}
        <div className="col-sm-6">{field('pincode', 'Pincode', { required })}</div>
      </div>
    </div>
  );
}
